import React from 'react';

export type BillingPeriod = 'MONTHLY' | 'ANNUAL';

export interface BillingPeriodSelectorProps {
  selectedPeriod: BillingPeriod | string;
  onPeriodChange: (period: BillingPeriod) => void;
  monthlyCost?: number;
  showPricing?: boolean;
  disabled?: boolean;
}

const CheckIcon: React.FC = () => (
  <div className="absolute top-2 right-2">
    <svg className="w-6 h-6 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  </div>
);

const BillingPeriodSelector: React.FC<BillingPeriodSelectorProps> = ({
  selectedPeriod,
  onPeriodChange,
  monthlyCost,
  showPricing = true,
  disabled = false,
}) => {
  const getOptionClassName = (period: BillingPeriod) =>
    `relative flex items-center justify-between p-4 border-2 rounded-lg cursor-pointer transition-all ${
      selectedPeriod === period
        ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
        : 'border-gray-300 dark:border-gray-600 hover:border-indigo-300 hover:bg-gray-50 dark:hover:bg-gray-800'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;

  const handleChange = (period: BillingPeriod) => {
    if (!disabled) {
      onPeriodChange(period);
    }
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        Billing Period <span className="text-red-500">*</span>
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Monthly Option */}
        <label
          className={getOptionClassName('MONTHLY')}
          onClick={() => handleChange('MONTHLY')}>
          <input
            type="radio"
            name="billingPeriod"
            value="MONTHLY"
            checked={selectedPeriod === 'MONTHLY'}
            onChange={() => handleChange('MONTHLY')}
            disabled={disabled}
            className="sr-only"
          />
          <div className="flex-1">
            <p className="font-semibold text-gray-900 dark:text-white">Monthly</p>
            {showPricing && monthlyCost !== undefined && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                ${monthlyCost.toFixed(2)}/month
              </p>
            )}
          </div>
          {selectedPeriod === 'MONTHLY' && <CheckIcon />}
        </label>

        {/* Annual Option */}
        <label
          className={getOptionClassName('ANNUAL')}
          onClick={() => handleChange('ANNUAL')}>
          <input
            type="radio"
            name="billingPeriod"
            value="ANNUAL"
            checked={selectedPeriod === 'ANNUAL'}
            onChange={() => handleChange('ANNUAL')}
            disabled={disabled}
            className="sr-only"
          />
          <div className="flex-1">
            <p className="font-semibold text-gray-900 dark:text-white">Annual</p>
            {showPricing && monthlyCost !== undefined && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                ${(monthlyCost * 12).toFixed(2)}/year
              </p>
            )}
          </div>
          {selectedPeriod === 'ANNUAL' && <CheckIcon />}
        </label>
      </div>
    </div>
  );
};

export default BillingPeriodSelector;
